import { FaceMesh, Results } from '@mediapipe/face_mesh';
import { FaceLandmarks, EmotionResult } from '../types';

export class EmotionDetector {
  private faceMesh: FaceMesh | null = null;
  private onEmotionCallback: ((emotion: EmotionResult) => void) | null = null;
  private emotionHistory: EmotionResult[] = [];

  async initialize(
    videoElement: HTMLVideoElement,
    onEmotion: (emotion: EmotionResult) => void
  ): Promise<void> {
    console.log('Initializing emotion detection...');
    this.onEmotionCallback = onEmotion;

    this.faceMesh = new FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      }
    });

    this.faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });

    this.faceMesh.onResults(this.processResults.bind(this));
    console.log('Emotion detection initialized');
  }

  async processFrame(videoElement: HTMLVideoElement): Promise<void> {
    if (this.faceMesh) {
      await this.faceMesh.send({ image: videoElement });
    }
  }

  private processResults(results: Results): void {
    if (!this.onEmotionCallback || !results.multiFaceLandmarks || results.multiFaceLandmarks.length === 0) {
      return;
    }

    const landmarks = results.multiFaceLandmarks[0];
    const emotion = this.detectEmotion(landmarks);
    
    // Smooth emotion detection
    const smoothedEmotion = this.smoothEmotion(emotion);
    
    if (smoothedEmotion.confidence > 0.6) {
      this.onEmotionCallback(smoothedEmotion);
    }
  }

  private detectEmotion(landmarks: any[]): EmotionResult {
    // Extract key facial features
    const leftEyeAspectRatio = this.calculateEyeAspectRatio(landmarks, 'left');
    const rightEyeAspectRatio = this.calculateEyeAspectRatio(landmarks, 'right');
    const mouthAspectRatio = this.calculateMouthAspectRatio(landmarks);
    const eyebrowHeight = this.calculateEyebrowHeight(landmarks);
    const mouthCurvature = this.calculateMouthCurvature(landmarks);

    // Rule-based emotion detection
    const avgEyeRatio = (leftEyeAspectRatio + rightEyeAspectRatio) / 2;

    // Happy - mouth curved up, eyes normal
    if (mouthCurvature > 0.02 && mouthAspectRatio < 0.3) {
      return {
        emotion: 'happy',
        confidence: 0.85,
        timestamp: Date.now()
      };
    }

    // Surprised - eyes wide, mouth open
    if (avgEyeRatio > 0.25 && mouthAspectRatio > 0.4) {
      return {
        emotion: 'surprised',
        confidence: 0.82,
        timestamp: Date.now()
      };
    }

    // Sad - mouth curved down, eyebrows down
    if (mouthCurvature < -0.01 && eyebrowHeight < -0.01) {
      return {
        emotion: 'sad',
        confidence: 0.78,
        timestamp: Date.now()
      };
    }

    // Angry - eyebrows down, mouth tight
    if (eyebrowHeight < -0.02 && mouthAspectRatio < 0.15) {
      return {
        emotion: 'angry',
        confidence: 0.75,
        timestamp: Date.now()
      };
    }

    // Fear - eyes wide, eyebrows up
    if (avgEyeRatio > 0.23 && eyebrowHeight > 0.02) {
      return {
        emotion: 'fear',
        confidence: 0.72,
        timestamp: Date.now()
      };
    }

    // Disgust - nose wrinkled, upper lip raised
    if (mouthCurvature < -0.005 && mouthAspectRatio < 0.2) {
      return {
        emotion: 'disgust',
        confidence: 0.70,
        timestamp: Date.now()
      };
    }

    // Neutral - default
    return {
      emotion: 'neutral',
      confidence: 0.65,
      timestamp: Date.now()
    };
  }

  private calculateEyeAspectRatio(landmarks: any[], eye: 'left' | 'right'): number {
    // Simplified eye aspect ratio calculation
    const indices = eye === 'left' 
      ? { top: 159, bottom: 145, left: 33, right: 133 }
      : { top: 386, bottom: 374, left: 362, right: 263 };

    const vertical = this.distance(landmarks[indices.top], landmarks[indices.bottom]);
    const horizontal = this.distance(landmarks[indices.left], landmarks[indices.right]);

    return vertical / horizontal;
  }

  private calculateMouthAspectRatio(landmarks: any[]): number {
    // Mouth opening ratio
    const top = landmarks[13];
    const bottom = landmarks[14];
    const left = landmarks[61];
    const right = landmarks[291];

    const vertical = this.distance(top, bottom);
    const horizontal = this.distance(left, right);

    return vertical / horizontal;
  }

  private calculateEyebrowHeight(landmarks: any[]): number {
    // Average eyebrow height relative to eyes
    const leftEyebrow = landmarks[70];
    const rightEyebrow = landmarks[300];
    const leftEye = landmarks[159];
    const rightEye = landmarks[386];

    const leftHeight = leftEye.y - leftEyebrow.y;
    const rightHeight = rightEye.y - rightEyebrow.y;

    return (leftHeight + rightHeight) / 2;
  }

  private calculateMouthCurvature(landmarks: any[]): number {
    // Mouth corner height relative to center
    const leftCorner = landmarks[61];
    const rightCorner = landmarks[291];
    const center = landmarks[13];

    const avgCornerY = (leftCorner.y + rightCorner.y) / 2;
    return center.y - avgCornerY;
  }

  private distance(p1: any, p2: any): number {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    const dz = (p1.z || 0) - (p2.z || 0);
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  private smoothEmotion(emotion: EmotionResult): EmotionResult {
    this.emotionHistory.push(emotion);

    // Keep only last 10 emotions
    if (this.emotionHistory.length > 10) {
      this.emotionHistory.shift();
    }

    // Count occurrences
    const counts = new Map<string, number>();
    const confidences = new Map<string, number[]>();

    this.emotionHistory.forEach(e => {
      counts.set(e.emotion, (counts.get(e.emotion) || 0) + 1);
      if (!confidences.has(e.emotion)) confidences.set(e.emotion, []);
      confidences.get(e.emotion)!.push(e.confidence);
    });

    // Find most common emotion
    let maxCount = 0;
    let smoothedEmotion = emotion.emotion;

    counts.forEach((count, name) => {
      if (count > maxCount) {
        maxCount = count;
        smoothedEmotion = name;
      }
    });

    // Average confidence
    const avgConfidence = confidences.get(smoothedEmotion)!.reduce((a, b) => a + b, 0) / 
                         confidences.get(smoothedEmotion)!.length;

    return {
      emotion: smoothedEmotion,
      confidence: avgConfidence,
      timestamp: Date.now()
    };
  }

  stop(): void {
    if (this.faceMesh) {
      this.faceMesh.close();
    }
  }
}
